import { catmatTalidomida } from 'api/ImpressaoMedicamentos'
import { Alert, Button, HFlow, Icon, ModalBody, ModalFooter, ModalHeader, ModalSidebar, VFlow } from 'bold-ui'
import { FieldArrayRenderProps } from 'components/form'
import { usePecField } from 'components/form/core/hooks/useField'
import { CiapSelectFieldModel } from 'components/form/field/select/CiapSelectField/CiapSelectField'
import { CidSelectFieldModel } from 'components/form/field/select/CidSelectField/CidSelectField'
import { confirm } from 'components/modals/confirm'
import { ButtonLink } from 'components/route'
import theme from 'config/theme'
import { css } from 'emotion'
import { usePrincipiosAtivosAlergiasQuery } from 'graphql/hooks.generated'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import React, { useCallback, useMemo, useState } from 'react'
import { useRouteMatch } from 'react-router'
import { TermosOutrosMedicamentosImpressaoPrescricao, TermosTalidomidaImpressaoPrescricao } from 'types/enums'
import { dedupById } from 'util/array'
import { isEmpty } from 'util/checks'
import { metaPath } from 'util/metaPath'
import { meta } from 'view/atendimentos/atendimento-individual/model'

import { ProblemaCondicaoModel } from '../../../avaliacao/components/problemas-condicoes/model-problemasCondicoes'
import MedicamentoCard from '../components/MedicamentoCard'
import { MedicamentoFormModel } from '../model-prescricao'
import { PrescricaoMedicamentoPlanoModel } from '../PrescricaoMedicamentoView'
import { hasIsotretinoinaTretinoinaAcitretina } from '../utils/utils-prescricao'
import PrescricaoMedicamentoForm from './PrescricaoMedicamentoForm'

const path = metaPath<PrescricaoMedicamentoPlanoModel>()

export interface AdicionarPrescricaoFieldArrayProps extends FieldArrayRenderProps<MedicamentoFormModel, any> {
  medicamentoToEdit: MedicamentoFormModel
  medicamentosToReplicate: MedicamentoFormModel[]
  setMedicamentoToEdit(medicamento: MedicamentoFormModel): void
  onClearMedicamentosToReplicate(): void
  onChangeMedicamentosToReplicate(medicamentos: MedicamentoFormModel[]): void
  onCloseModal(): void
  scrollModal(): void
}

export default function AdicionarPrescricaoFieldArray(props: AdicionarPrescricaoFieldArrayProps) {
  const {
    fields: { push, update, remove, value },
    onChangeMedicamentosToReplicate,
    onClearMedicamentosToReplicate,
    medicamentosToReplicate,
    medicamentoToEdit,
    onCloseModal,
    setMedicamentoToEdit,
    scrollModal,
  } = props
  const { analytics } = useAnalytics()
  const match = useRouteMatch()
  const [showDoseAlert, setShowDoseAlert] = useState(
    medicamentoToEdit?.dose && !medicamentoToEdit?.qtDose && !medicamentoToEdit?.unidadeMedidaDose
  )

  const {
    input: { value: alreadyPrinted, onChange: onChangeAlreadyPrinted },
  } = usePecField({ name: path.alreadyPrinted })

  const urlToEncaminhar = useMemo(() => match.url.replace('/adicionar', '/encaminhar'), [match.url])

  const {
    prontuario: { id: prontuarioId },
  } = useAtendimentoContext()

  const {
    data: { principiosAtivosAlergias },
  } = usePrincipiosAtivosAlergiasQuery({
    variables: { prontuarioId: prontuarioId },
  })

  const {
    input: { value: pesoAtual },
  } = usePecField({
    name: meta.objetivo.antropometria.peso,
  })

  const {
    input: { value: pesoAoNascer },
  } = usePecField({
    name: meta.objetivo.atendimentosEspecificos.puericultura.prenatalPartoNascimento.antropometriaAoNascer.peso,
  })

  const {
    input: { value: medicoesAnteriores },
  } = usePecField({
    name: meta.medicoesAnteriores,
  })

  const {
    input: { value: alergiasAtendimentoAtual },
  } = usePecField({
    name: meta.avaliacao.alergias,
  })

  const {
    input: { value: problemasECondicoes },
  } = usePecField<ProblemaCondicaoModel[]>({
    name: meta.avaliacao.problemasECondicoes,
    subscription: { value: true },
  })

  const cidsRegistrados = useMemo<CidSelectFieldModel[]>(
    () => dedupById((problemasECondicoes ?? []).map((p) => p.cid)),
    [problemasECondicoes]
  )

  const ciapsRegistrados = useMemo<CiapSelectFieldModel[]>(
    () => dedupById((problemasECondicoes ?? []).map((p) => p.ciap)),
    [problemasECondicoes]
  )

  const isAlergico = useCallback(
    (medicamento: MedicamentoFormModel) => {
      const principioId = medicamento.principioAtivoCombo?.principioAtivo?.id

      if (!principioId) return false

      return (
        (principiosAtivosAlergias?.some((principio) => principio.id === principioId) ||
          alergiasAtendimentoAtual?.some(
            (alergia) => alergia.substanciaMedicamento?.principioAtivo?.id === principioId
          )) ??
        false
      )
    },
    [alergiasAtendimentoAtual, principiosAtivosAlergias]
  )

  const removeAlreadyPrinted = useCallback(
    (medicamento: MedicamentoFormModel) => {
      const { id, principioAtivoCombo } = medicamento

      const toRemove: ID[] = []

      if (!isEmpty(alreadyPrinted) && alreadyPrinted?.find((item: ID) => item === id)) {
        toRemove.push(id)
      }
      if (principioAtivoCombo?.codigoCatMat === catmatTalidomida) {
        toRemove.push(...Object.keys(TermosTalidomidaImpressaoPrescricao))
      }
      if (hasIsotretinoinaTretinoinaAcitretina([medicamento])) {
        toRemove.push(...Object.keys(TermosOutrosMedicamentosImpressaoPrescricao))
      }

      onChangeAlreadyPrinted(alreadyPrinted.filter((item: ID) => !toRemove.includes(item)))
    },
    [alreadyPrinted, onChangeAlreadyPrinted]
  )

  const handleOnChangeEditedMedicamento = useCallback(
    (editedMedicamento: MedicamentoFormModel) => {
      setMedicamentoToEdit(editedMedicamento)
      setShowDoseAlert(editedMedicamento?.dose && !editedMedicamento?.qtDose && !editedMedicamento?.unidadeMedidaDose)
    },
    [setMedicamentoToEdit]
  )

  const handleOnRemoveMedicamento = useCallback(
    (medicamentoToRemove: MedicamentoFormModel) => {
      const index = value.findIndex((item) => item.id === medicamentoToRemove.id)
      remove(index)

      if (!isEmpty(alreadyPrinted) && alreadyPrinted.includes(medicamentoToRemove.id)) {
        removeAlreadyPrinted(medicamentoToRemove)
      }

      if (medicamentoToEdit?.id === medicamentoToRemove.id) {
        setMedicamentoToEdit(null)
        setShowDoseAlert(false)
      }
    },
    [alreadyPrinted, medicamentoToEdit, remove, removeAlreadyPrinted, setMedicamentoToEdit, value]
  )

  const handleOnRemoveMedicamentoToReplicate = useCallback(
    (medicamento: MedicamentoFormModel) => {
      const medicamentos = medicamentosToReplicate.filter((item) => item.id !== medicamento.id)
      if (isEmpty(medicamentos)) {
        onClearMedicamentosToReplicate()
      } else {
        onChangeMedicamentosToReplicate(medicamentos)
      }

      if (!isEmpty(alreadyPrinted) && alreadyPrinted.includes(medicamento.id)) {
        removeAlreadyPrinted(medicamento.id)
      }

      if (medicamentoToEdit?.id === medicamento.id) {
        setMedicamentoToEdit(null)
        setShowDoseAlert(false)
      }
    },
    [
      alreadyPrinted,
      medicamentoToEdit,
      medicamentosToReplicate,
      onChangeMedicamentosToReplicate,
      onClearMedicamentosToReplicate,
      removeAlreadyPrinted,
      setMedicamentoToEdit,
    ]
  )

  const handleOnSubmit = useCallback(
    (medicamento: MedicamentoFormModel) => {
      if (medicamentoToEdit) {
        setShowDoseAlert(medicamentoToEdit?.dose && !medicamentoToEdit?.qtDose && !medicamentoToEdit?.unidadeMedidaDose)
        if (medicamentosToReplicate && medicamentosToReplicate.find((item) => item.id === medicamento.id)) {
          push(medicamento)
          handleOnRemoveMedicamentoToReplicate(medicamento)
        } else {
          const index = value?.findIndex((item) => item.id === medicamento.id)

          if (!isEmpty(alreadyPrinted) && alreadyPrinted.includes(medicamento.id)) {
            removeAlreadyPrinted(medicamento.id)
          }

          update(index, medicamento)
        }
      } else {
        push(medicamento)
      }
    },
    [
      alreadyPrinted,
      handleOnRemoveMedicamentoToReplicate,
      medicamentoToEdit,
      medicamentosToReplicate,
      push,
      removeAlreadyPrinted,
      update,
      value,
    ]
  )

  const handleOnMedicamentoDuplicated = useCallback(
    (medicamentoToAdd: MedicamentoFormModel) => {
      return value?.some(
        (receita) =>
          receita.id !== medicamentoToAdd.id &&
          !medicamentoToAdd.registroManual &&
          !receita.registroManual &&
          receita.principioAtivoCombo?.medicamento.id === medicamentoToAdd.principioAtivoCombo?.medicamento.id
      )
    },
    [value]
  )

  const confirmRemoveMedicamento = useCallback(
    (isReplicar: boolean) => (medicamento: MedicamentoFormModel) => {
      confirm({
        title: 'Deseja excluir este medicamento?',
        body: 'Este medicamento será excluído da prescrição do atendimento.',
        type: 'primary',
        onConfirm: () => {
          isReplicar ? handleOnRemoveMedicamentoToReplicate(medicamento) : handleOnRemoveMedicamento(medicamento)
        },
        confirmLabel: 'Excluir',
        depthLevel: 2,
        manageOverflow: false,
      })()
    },
    [handleOnRemoveMedicamento, handleOnRemoveMedicamentoToReplicate]
  )

  return useMemo(
    () => (
      <React.Fragment>
        <ModalHeader title='Adicionar prescrição' />
        <ModalBody style={styles.formContainer}>
          <VFlow vSpacing={2}>
            <PrescricaoMedicamentoForm
              onSubmit={handleOnSubmit}
              isReplicar={!isEmpty(medicamentosToReplicate)}
              isRenovacao={!!medicamentoToEdit?.receitaUsoContinuoGrupoId}
              medicamentoToEdit={medicamentoToEdit}
              pesoAtual={pesoAtual}
              pesoAoNascer={pesoAoNascer}
              medicoesAnteriores={medicoesAnteriores}
              cidsRegistrados={cidsRegistrados}
              ciapsRegistrados={ciapsRegistrados}
              onClearEditedMedicamento={handleOnChangeEditedMedicamento}
              onVerifyMedicamentoDuplicated={handleOnMedicamentoDuplicated}
              scrollModal={scrollModal}
              showDoseAlert={showDoseAlert}
            />
          </VFlow>
        </ModalBody>
        <ModalSidebar position='right'>
          <VFlow vSpacing={0.5}>
            {medicamentosToReplicate && !isEmpty(medicamentosToReplicate) && (
              <Alert type='warning' inline>
                Revise os dados antes de salvar uma nova prescrição.
              </Alert>
            )}
            {value?.map((item) => (
              <MedicamentoCard
                key={item.id}
                editing={medicamentoToEdit?.id === item.id}
                medicamentoModel={item}
                isAlergico={isAlergico(item)}
                onRemoveMedicamento={confirmRemoveMedicamento(false)}
                onEditMedicamento={setMedicamentoToEdit}
                setShowDoseAlert={setShowDoseAlert}
              />
            ))}
            {medicamentosToReplicate &&
              !isEmpty(medicamentosToReplicate) &&
              medicamentosToReplicate.map((item) => (
                <MedicamentoCard
                  key={item.id}
                  editing={medicamentoToEdit?.id === item.id}
                  replicating
                  medicamentoModel={item}
                  isAlergico={isAlergico(item)}
                  onRemoveMedicamento={confirmRemoveMedicamento(true)}
                  onEditMedicamento={setMedicamentoToEdit}
                  setShowDoseAlert={setShowDoseAlert}
                />
              ))}
          </VFlow>
        </ModalSidebar>
        <ModalFooter>
          <HFlow justifyContent='flex-end'>
            <Button onClick={onCloseModal} style={styles.footerButton} data-cy='ModalFooter.FecharButton'>
              Fechar
            </Button>
            <ButtonLink
              kind='primary'
              style={styles.footerButton}
              to={urlToEncaminhar}
              disabled={!value?.length}
              data-cy='EncaminharButton'
              onClick={() => analytics.logEvent('click_button_encaminhar_prescricao')}
            >
              <Icon icon='printerOutline' style={styles.printIcon} />
              Encaminhar
            </ButtonLink>
          </HFlow>
        </ModalFooter>
      </React.Fragment>
    ),
    [
      handleOnSubmit,
      medicamentosToReplicate,
      medicamentoToEdit,
      pesoAtual,
      pesoAoNascer,
      medicoesAnteriores,
      cidsRegistrados,
      ciapsRegistrados,
      handleOnChangeEditedMedicamento,
      handleOnMedicamentoDuplicated,
      scrollModal,
      showDoseAlert,
      value,
      onCloseModal,
      urlToEncaminhar,
      isAlergico,
      confirmRemoveMedicamento,
      setMedicamentoToEdit,
      analytics,
    ]
  )
}

const styles = {
  formContainer: css`
    width: 42rem;
    background-color: ${theme.pallete.surface.main};
    border-right: 1px solid + ${theme.pallete.gray.c80};
  `,
  printIcon: css`
    margin-right: 0.5rem;
  `,
  footerButton: css`
    width: 9rem;
    height: 3rem;
  `,
}
